import { useEffect } from 'react';
import { AuditEntityType, LeadLiveArea, LeadLiveChangeDto, LEAD_LIVE_CHANGED, LEAD_LIVE_SUBSCRIBE, LEAD_LIVE_UNSUBSCRIBE, LEAD_LIVE_UNAVAILABLE } from '@sr/dto';
import { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { useAppDispatch } from 'shared/store/hooks';
import { acquireRealtimeConnection } from 'shared/api/realtime';
import { auditApi } from 'entities/audit/api';
import { commentsApi } from 'entities/comments/api';
import { contactsApi } from 'features/contact/contacts.api-slice';
import { buildGetContactsUrl } from 'features/contact/contact.helpers';
import { EntitiesWithContacts } from 'features/contact/contacts.types';
import { leadApi } from './api';
import { leadConnectionsApi } from './lead-companies/api';
import { leadMediaplanApi } from './lead-mediaplan/api';

const ALL_AREAS: LeadLiveArea[] = ['lead', 'comments', 'contacts', 'audit'];

export const refreshLiveLead = (dispatch: Dispatch<UnknownAction>, leadId: number, areas: LeadLiveArea[]) => {
  if (areas.includes('lead')) {
    dispatch(leadApi.util.invalidateTags([{ type: 'lead', id: leadId }, { type: 'leadInvoices', id: leadId }]));
    dispatch(leadConnectionsApi.util.invalidateTags([{ type: 'leadContacts', id: leadId }]));
    dispatch(leadMediaplanApi.util.invalidateTags([{ type: 'leadMediaPlan', id: leadId }]));
  }
  if (areas.includes('comments')) dispatch(commentsApi.util.invalidateTags([{ type: 'lead-comments', id: leadId }]));
  if (areas.includes('contacts')) dispatch(contactsApi.util.invalidateTags([
    { type: 'Contacts', id: buildGetContactsUrl({ relationType: EntitiesWithContacts.lead, relationId: leadId }) },
  ]));
  if (areas.includes('audit')) dispatch(auditApi.util.invalidateTags([{ type: 'AuditLog', id: `${AuditEntityType.LEAD}-${leadId}` }]));
};

export const useLeadLiveUpdates = (leadId?: number) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!leadId) return;
    const { socket, release } = acquireRealtimeConnection();
    let active = true;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const pending = new Set<LeadLiveArea>();
    // Several committed changes (status + system comment, Wazzup + phone) can
    // arrive together. Coalesce their cache refreshes; this is not polling.
    const scheduleRefresh = (areas: LeadLiveArea[]) => {
      areas.forEach((area) => pending.add(area));
      if (refreshTimer !== undefined) return;
      refreshTimer = setTimeout(() => {
        refreshTimer = undefined;
        if (active) refreshLiveLead(dispatch, leadId, [...pending]);
        pending.clear();
      }, 50);
    };
    const subscribe = () => socket.emit(LEAD_LIVE_SUBSCRIBE, { leadId }, (reply: { ok?: boolean }) => {
      // Refresh after subscribing/reconnecting to cover changes while offline.
      if (active && reply) scheduleRefresh(ALL_AREAS);
    });
    const onChanged = (change: LeadLiveChangeDto) => {
      if (change?.leadId === leadId && Array.isArray(change.areas)) scheduleRefresh(change.areas);
    };
    const onUnavailable = (change: { leadId: number }) => {
      if (change?.leadId === leadId) scheduleRefresh(ALL_AREAS);
    };
    socket.on('connect', subscribe);
    socket.on(LEAD_LIVE_CHANGED, onChanged);
    socket.on(LEAD_LIVE_UNAVAILABLE, onUnavailable);
    if (socket.connected) subscribe();
    return () => {
      active = false;
      if (refreshTimer !== undefined) clearTimeout(refreshTimer);
      socket.off('connect', subscribe);
      socket.off(LEAD_LIVE_CHANGED, onChanged);
      socket.off(LEAD_LIVE_UNAVAILABLE, onUnavailable);
      if (socket.connected) socket.emit(LEAD_LIVE_UNSUBSCRIBE, { leadId });
      release();
    };
  }, [dispatch, leadId]);
};
